import { fullNameOf } from '@/utils/personName.ts';
import { BookingChangeListDto } from '@/features/dashboard/bookingChanges/types/dto/BookingChangeListDto.ts';
import {
  ChangePackageHistoryVm,
  ChangePackageStatus,
} from '@/features/dashboard/bookingChanges/types/ChangeViewModel.ts';
import { toChangePackageStatus } from '@/features/dashboard/bookingChanges/types/BookingChangeDetailDto.mapper.ts';

/**
 * Numer paczki wynika z jej pozycji na liście, bo backend takiej kolumny nie ma
 * i mieć nie będzie. Jest to trwałe: anulowanie ustawia `status = CANCELLED`,
 * wiersza nie kasuje, więc raz nadany numer już się nie przesunie. Lista
 * przychodzi posortowana malejąco po dacie utworzenia, więc numer 1 dostaje
 * ostatnia pozycja.
 *
 * Numer musi wynikać z **kompletu** paczek rezerwacji — razem ze szkicami
 * i anulowanymi. Policzony po liście przefiltrowanej do wyświetlenia (poniższe
 * `toHistoryList` odsiewa szkice) przesuwałby się przy każdej paczce, której
 * widok nie pokazuje.
 */
const packageNumberAt = (
  packages: BookingChangeListDto[],
  index: number,
): number => packages.length - index;

/** Numer jednej paczki; `null`, gdy nie ma jej na liście rezerwacji. */
export const packageNumberOf = (
  packages: BookingChangeListDto[],
  changeId: string | undefined,
): number | null => {
  const index = packages.findIndex((dto) => dto.id === changeId);
  return index === -1 ? null : packageNumberAt(packages, index);
};

/**
 * Mapper historii paczek — karta „Ostatnie zmiany” i tabela historii.
 *
 * `bookedDelta` jest listą po jednej pozycji na walutę i przechodzi listą —
 * paczka potrafi ruszyć złotówki i euro naraz, więc wiersz historii pokazuje
 * obie kwoty. `null`
 * przechodzi przez mapper **jako `null`**, a nie jako zero: kontrakt wypełnia
 * kwotę wyłącznie dla paczki wprowadzonej, więc przy anulowanej i odrzuconej
 * jej po prostu nie ma, a „0,00 zł” mówiłoby, że paczka nic nie ruszyła.
 *
 * Historia to wyłącznie paczki zamknięte. Otwarty szkic przychodzi w tej samej
 * liście, ale ogłasza się osobno — belką pod zakładkami i wyróżnionym wierszem
 * karty „Ostatnie zmiany” — więc wpuszczenie go do historii dublowałoby go na
 * ekranie i zapowiadało paczkę, której jeszcze nie ma.
 */
export const bookingChangeHistoryMapper = {
  toHistoryVm(
    dto: BookingChangeListDto,
    packageNumber: number,
  ): ChangePackageHistoryVm {
    return {
      id: dto.id,
      packageNumber,
      status: toChangePackageStatus(dto.status),
      kind: dto.kind,
      author: dto.author
        ? fullNameOf(dto.author.firstName, dto.author.lastName)
        : null,
      // Paczka wprowadzona ma własną datę wejścia w życie; pozostałe znamy
      // wyłącznie od strony założenia.
      occurredAt: dto.appliedAt ?? dto.createdAt,
      itemCount: dto.itemCount,
      impact: dto.bookedDelta,
      rejectionComment: dto.rejectionComment,
    };
  },

  toHistoryList(dtos: BookingChangeListDto[]): ChangePackageHistoryVm[] {
    return dtos
      .map((dto, index) => this.toHistoryVm(dto, packageNumberAt(dtos, index)))
      .filter((entry) => entry.status !== ChangePackageStatus.DRAFT);
  },
};
